import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Layers as LayersIcon,
  Storage as StorageIcon,
  Extension as ExtensionIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import { RegistryOverview } from './hooks/useAIObservatory';

interface AgentRegistryPanelProps {
  registry: RegistryOverview | null;
}

export const AgentRegistryPanel: React.FC<AgentRegistryPanelProps> = ({ registry }) => {
  if (!registry) {
    return (
      <Card>
        <CardHeader title={<><LayersIcon /> Provider Registry</>} />
        <CardContent>
          <Typography>Loading registry data...</Typography>
        </CardContent>
      </Card>
    );
  }

  const totalProviders =
    registry.tenantTypeProviders.length +
    registry.applicationProviders.length +
    registry.featureProviders.length;

  return (
    <Card>
      <CardHeader
        title="Provider Registry"
        action={<Chip label={`${totalProviders} providers`} color="primary" />}
      />
      <CardContent>
        {/* Tenant Type Providers */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <StorageIcon />
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1">Tenant Type Providers</Typography>
                <Typography variant="caption" color="text.secondary">
                  Base providers for tenant types
                </Typography>
              </Box>
              <Chip label={registry.tenantTypeProviders.length} color="primary" size="small" />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {registry.tenantTypeProviders.length === 0 ? (
              <Typography color="text.secondary">No tenant type providers registered</Typography>
            ) : (
              <List>
                {registry.tenantTypeProviders.map((provider, index) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={provider.tenantType}
                      secondary={
                        <>
                          <Typography variant="body2">Assistant Key: {provider.assistantKey}</Typography>
                          {provider.functions && provider.functions.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                              {provider.functions.slice(0, 3).map((func: any, i: number) => (
                                <Chip
                                  key={i}
                                  icon={<CodeIcon />}
                                  label={func.name}
                                  size="small"
                                  variant="outlined"
                                  color="info"
                                />
                              ))}
                              {provider.functions.length > 3 && (
                                <Chip label={`+${provider.functions.length - 3} more`} size="small" variant="outlined" />
                              )}
                            </Box>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Application Providers */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <ExtensionIcon />
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1">Application Providers</Typography>
                <Typography variant="caption" color="text.secondary">
                  Application-specific AI capabilities
                </Typography>
              </Box>
              <Chip label={registry.applicationProviders.length} color="secondary" size="small" />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {registry.applicationProviders.length === 0 ? (
              <Typography color="text.secondary">No application providers registered</Typography>
            ) : (
              <List>
                {registry.applicationProviders.map((provider, index) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={provider.applicationKey}
                      secondary={
                        <>
                          <Typography variant="body2">Assistant Key: {provider.assistantKey}</Typography>
                          {provider.functions && provider.functions.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                              {provider.functions.slice(0, 3).map((func: any, i: number) => (
                                <Chip
                                  key={i}
                                  icon={<CodeIcon />}
                                  label={func.name}
                                  size="small"
                                  variant="outlined"
                                  color="info"
                                />
                              ))}
                              {provider.functions.length > 3 && (
                                <Chip label={`+${provider.functions.length - 3} more`} size="small" variant="outlined" />
                              )}
                            </Box>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Feature Providers */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <ExtensionIcon />
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1">Feature Providers</Typography>
                <Typography variant="caption" color="text.secondary">
                  Feature-specific AI capabilities
                </Typography>
              </Box>
              <Chip label={registry.featureProviders.length} color="info" size="small" />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {registry.featureProviders.length === 0 ? (
              <Typography color="text.secondary">No feature providers registered</Typography>
            ) : (
              <List>
                {registry.featureProviders.map((provider, index) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {provider.featureName || provider.featureKey}
                          </Typography>
                          {provider.functions && provider.functions.length > 0 && (
                            <Chip 
                              label={`${provider.functions.length} functions`} 
                              size="small" 
                              color="success"
                            />
                          )}
                          {provider.applicationKey && (
                            <Chip label={`App: ${provider.applicationKey}`} size="small" variant="outlined" />
                          )}
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            Feature Key: {provider.featureKey}
                          </Typography>
                          {provider.description && (
                            <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 0.5, color: 'text.secondary' }}>
                              {provider.description}
                            </Typography>
                          )}
                          {provider.functions && provider.functions.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                Available Functions:
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {provider.functions.map((func: any, i: number) => (
                                  <Chip
                                    key={i}
                                    icon={<CodeIcon />}
                                    label={func.name}
                                    size="small"
                                    variant="outlined"
                                    color="info"
                                    title={func.description || func.name}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </AccordionDetails>
        </Accordion>

      </CardContent>
    </Card>
  );
};
